type InputKeys = "meetingId" |
    "tokenId" |
    "num_items" |
    "render_time" |
    "color" |
    "bgColor" |
    "marginTop" |
    "marginRight" |
    "marginBottom" |
    "marginLeft" |
    "marginGuides";

type InputsMap = { 
  meetingId: string;
  tokenId: string;
  num_items: number;
  render_time: boolean;
  bgColor: string;
  color: string;
  marginTop: number;
  marginRight: number;
  marginBottom: number;
  marginLeft: number;
  marginGuides: boolean;
};

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGB_RE = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i;
const NS = "mmip:";

function isValidRgbTuple(r: number, g: number, b: number) {
     return [r, g, b].every(n => Number.isFinite(n) && n >= 0 && n <= 255);
}

function isValidAlpha(a: number) {
    return Number.isFinite(a) && a >= 0 && a <= 1;
}

export const paramsBus = new EventTarget();

export const defaultParams: Partial<InputsMap> = {
  num_items:    +(import.meta.env.VITE_DEFAULT_UI_MAXITEMS ?? 99),
  render_time:  (import.meta.env.VITE_DEFAULT_UI_SHOWTIME === "true"),
  bgColor:      import.meta.env.VITE_DEFAULT_UI_BGCOLOR,
  color:        import.meta.env.VITE_DEFAULT_UI_COLOR,
  marginTop:    +(import.meta.env.VITE_DEFAULT_UI_MTOP ?? 0),
  marginRight:  +(import.meta.env.VITE_DEFAULT_UI_MRIGHT ?? 30),
  marginBottom: +(import.meta.env.VITE_DEFAULT_UI_MBOTTOM ?? 0),
  marginLeft:   +(import.meta.env.VITE_DEFAULT_UI_MLEFT ?? 0),
  marginGuides: (import.meta.env.VITE_DEFAULT_UI_SHOWGUIDES === "true"),
};

export function ensureDefaults() {
  (Object.keys(defaultParams) as (keyof typeof defaultParams)[]).forEach((key) => {
    const existing = getParam(key as any);
    if (existing === undefined || existing === null) {
      const def = defaultParams[key];
      if (def !== undefined) setParam(key as any, def as any);
    }
  });
}

export const coercers: { [K in InputKeys]:
    (s: string) => InputsMap[K] | undefined } = {
    meetingId: s => s,
    tokenId: s => s,
    num_items: s => {
        const n = Math.floor(Number(s));
        return Number.isFinite(n) && n > 0 ? n : undefined;
    },
    render_time: s => {
        if (/^(1|true|yes)$/i.test(s)) return true;
        if (/^(0|false|no)$/i.test(s)) return false;
        return undefined; // invalid -> no override
    },
    bgColor: s => {
        if (HEX_RE.test(s)) return s;

        const m = s.match(RGB_RE);
        if (m) {
            const r = +m[1], g = +m[2], b = +m[3];
            const a = m[4] === undefined ? undefined : +m[4];
            if (isValidRgbTuple(r, g, b) && (a === undefined || isValidAlpha(a))) return s;
        }

        return undefined; // invalid -> do not override stored value
    },
    color: s => {
        if (HEX_RE.test(s)) return s;

        const m = s.match(RGB_RE);
        if (m) {
            const r = +m[1], g = +m[2], b = +m[3];
            const a = m[4] === undefined ? undefined : +m[4];
            if (isValidRgbTuple(r, g, b) && (a === undefined || isValidAlpha(a))) return s;
        }

        return undefined; // invalid -> do not override stored value
    },
    marginTop: s => {
        const n = Math.floor(Number(s));
        return Number.isFinite(n) && n > 0 ? n : undefined;
    },
    marginRight: s => {
        const n = Math.floor(Number(s));
        return Number.isFinite(n) && n > 0 ? n : undefined;
    },
    marginBottom: s => {
        const n = Math.floor(Number(s));
        return Number.isFinite(n) && n > 0 ? n : undefined;
    },
    marginLeft: s => {
        const n = Math.floor(Number(s));
        return Number.isFinite(n) && n > 0 ? n : undefined;
    },
    marginGuides: s => {
        if (/^(1|true|yes)$/i.test(s)) return true;
        if (/^(0|false|no)$/i.test(s)) return false;
        return undefined; // invalid -> no override
    },
};

function parseInput<K extends InputKeys>(key: K, value: string | null): InputsMap[K] | undefined {
    return value == null ? undefined : coercers[key](value);
}

export function getParam<K extends InputKeys>(key: K): InputsMap[K] | undefined {
    const params = new URL(location.href).searchParams;
    if (params.has(key)) {
        const v = parseInput(key, params.get(key));
        if (v !== undefined) return v; // only override if valid
    }
    try {
        const raw = localStorage.getItem(NS + key);
        return raw ? (JSON.parse(raw) as InputsMap[K]) : undefined;
    } catch {
        localStorage.removeItem(NS + key);
        return undefined;
    }
}

export function setParam<K extends InputKeys>(key: K, value: InputsMap[K] | undefined) {
    if (value === undefined || (typeof value === "string" && value.trim() === "")) {
        localStorage.removeItem(NS + key);
    } else {
        localStorage.setItem(NS + key, JSON.stringify(value));
    }

    paramsBus.dispatchEvent(new CustomEvent("changed", { detail: { key, value } }));
}

export function removeParam(key: InputKeys) {
    localStorage.removeItem(NS + key);
}

/** Optional helper if you need to know whether URL is taking precedence */
export const hasUrlOverride = (k: InputKeys) => new URL(location.href).searchParams.has(k);
